import { zodResolver } from '@hookform/resolvers/zod';
import { CircleCheckBig, CircleX, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTitle } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  REVIEW_DECISION,
  REVIEWER_COMMENT_MAX_LENGTH,
  UPDATE_SUBMISSION_TYPE,
} from '../constants/instituteSubmission.constants';
import { useReviewInstituteSubmission } from '../hooks/useReviewInstituteSubmission';
import {
  approveSubmissionSchema,
  rejectSubmissionSchema,
  type InstituteSubmissionDetail,
  type ReviewDecision,
  type SubmissionReviewPayload,
} from '../types/instituteSubmission.types';

const DECISION_POPOVER_GAP_PX = 8;
const REMOVAL_APPROVAL_UNAVAILABLE_TEXT =
  'Approving an inactivate or delete request is not available yet';

interface DecisionCopy {
  title: string;
  placeholder: string;
  variant: 'default' | 'destructive';
  isCommentRequired: boolean;
}

const DECISION_COPY: Record<ReviewDecision, DecisionCopy> = {
  APPROVE: {
    title: 'Approve submission',
    placeholder: 'Add a note for the ZM',
    variant: 'default',
    isCommentRequired: false,
  },
  REJECT: {
    title: 'Reject submission',
    placeholder: 'Tell the ZM why it was rejected',
    variant: 'destructive',
    isCommentRequired: true,
  },
};

interface InstituteSubmissionReviewActionsProps {
  submission: InstituteSubmissionDetail;
}

export function InstituteSubmissionReviewActions({
  submission,
}: InstituteSubmissionReviewActionsProps) {
  const [decision, setDecision] = useState<ReviewDecision | null>(null);
  const [pendingDiscard, setPendingDiscard] = useState<ReviewDecision | 'close' | null>(null);
  const [actionsSection, setActionsSection] = useState<HTMLElement | null>(null);
  const isValidation = submission.submissionType === UPDATE_SUBMISSION_TYPE;
  const decisionCopy = decision ? DECISION_COPY[decision] : null;

  const form = useForm<SubmissionReviewPayload>({
    resolver: zodResolver(
      decision === REVIEW_DECISION.REJECT ? rejectSubmissionSchema : approveSubmissionSchema,
    ),
    defaultValues: { comment: '' },
  });
  const comment = form.watch('comment');
  const hasComment = comment.trim().length > 0;
  const reviewMutation = useReviewInstituteSubmission();

  function openDecision(next: ReviewDecision) {
    form.reset({ comment: '' });
    setPendingDiscard(null);
    setDecision(next);
  }

  function closeDecision() {
    if (reviewMutation.isPending) return;
    setPendingDiscard(null);
    setDecision(null);
  }

  function requestDecision(next: ReviewDecision) {
    if (next === decision) return;
    if (decision && hasComment) {
      setPendingDiscard(next);
      return;
    }
    openDecision(next);
  }

  function requestClose() {
    if (reviewMutation.isPending) return;
    if (hasComment) {
      setPendingDiscard('close');
      return;
    }
    closeDecision();
  }

  function confirmDiscard() {
    if (pendingDiscard === 'close') closeDecision();
    else if (pendingDiscard) openDecision(pendingDiscard);
  }

  function submitDecision() {
    if (!decision) return;
    void form.handleSubmit((payload) => {
      reviewMutation.mutate({ id: submission.id, decision, payload }, { onSuccess: closeDecision });
    })();
  }

  function handleApproveClick() {
    if (!isValidation) {
      toast.info(REMOVAL_APPROVAL_UNAVAILABLE_TEXT);
      return;
    }
    requestDecision(REVIEW_DECISION.APPROVE);
  }

  return (
    <>
      {decisionCopy && (
        <div
          aria-hidden
          className="absolute inset-0 z-10 bg-foreground/20 duration-100 animate-in fade-in-0"
        />
      )}
      <section ref={setActionsSection} aria-label="Review" className="relative z-20">
        <div className="flex flex-wrap items-center justify-end gap-2 max-sm:grid max-sm:grid-cols-2">
          <Button
            type="button"
            variant="destructive"
            disabled={reviewMutation.isPending}
            onClick={() => requestDecision(REVIEW_DECISION.REJECT)}
            className="max-sm:h-10"
          >
            <CircleX />
            Reject
          </Button>
          <Button
            type="button"
            disabled={reviewMutation.isPending}
            aria-disabled={!isValidation || undefined}
            title={isValidation ? undefined : REMOVAL_APPROVAL_UNAVAILABLE_TEXT}
            onClick={handleApproveClick}
            className={cn('max-sm:h-10', !isValidation && 'cursor-not-allowed opacity-50')}
          >
            <CircleCheckBig />
            Approve
          </Button>
        </div>

        <Popover
          open={decisionCopy !== null}
          onOpenChange={(next, { event }) => {
            if (next) return;
            const target = event instanceof FocusEvent ? event.relatedTarget : event.target;
            if (target instanceof Node && actionsSection?.contains(target)) return;
            requestClose();
          }}
        >
          {decisionCopy && (
            <PopoverContent
              anchor={actionsSection}
              side="top"
              align="end"
              sideOffset={DECISION_POPOVER_GAP_PX}
              className="w-(--anchor-width) gap-3 rounded-xl p-4 shadow-lg"
            >
              <div className="flex items-center gap-2 px-0.5">
                <PopoverTitle className="text-sm font-medium">{decisionCopy.title}</PopoverTitle>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-2xs font-medium',
                    decisionCopy.isCommentRequired
                      ? 'bg-destructive/10 text-destructive'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {decisionCopy.isCommentRequired ? 'Required' : 'Optional'}
                </span>
              </div>

              <Form {...form}>
                <FormField
                  control={form.control}
                  name="comment"
                  render={({ field }) => (
                    <FormItem className="gap-1.5">
                      <FormLabel className="sr-only">Comment</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          autoFocus
                          maxLength={REVIEWER_COMMENT_MAX_LENGTH}
                          placeholder={decisionCopy.placeholder}
                          disabled={reviewMutation.isPending}
                          className="field-sizing-fixed min-h-40 resize-none overflow-y-auto rounded-lg border-transparent bg-muted/60 focus-visible:border-ring focus-visible:bg-transparent"
                        />
                      </FormControl>
                      <FormMessage className="px-0.5" />
                    </FormItem>
                  )}
                />
              </Form>

              <div className="flex min-h-8 items-center justify-between gap-2">
                {pendingDiscard ? (
                  <>
                    <p className="px-0.5 text-xs text-muted-foreground">Discard your comment?</p>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingDiscard(null)}
                      >
                        Keep
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={confirmDiscard}
                        className="text-destructive hover:text-destructive"
                      >
                        Discard
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <CharacterCounter
                      current={comment.length}
                      max={REVIEWER_COMMENT_MAX_LENGTH}
                      className="px-0.5"
                    />
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={requestClose}
                        disabled={reviewMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={decisionCopy.variant}
                        onClick={submitDecision}
                        disabled={reviewMutation.isPending}
                      >
                        {reviewMutation.isPending && (
                          <Loader2 className="animate-spin" aria-hidden />
                        )}
                        {reviewMutation.isPending ? 'Submitting...' : 'Submit'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </PopoverContent>
          )}
        </Popover>
      </section>
    </>
  );
}
